"use client"

import React, { useMemo } from "react"
import { motion, AnimatePresence } from "motion/react"
import { useTooltip } from "@/components/Tooltip"
import { historicalRdMultipleData } from "@/panelData";
import { thresholds } from "../HUD";
import { ChinaLogo, OpenAILogo, WorldLogo } from "@/components/icons";
import { FloatingPortal } from "@floating-ui/react";

interface SpeedupDatum {
  date: string;
  OpenBrain: number;
  US2nd: number;
  China: number;
}

interface ComputeUsageByActorLineChartProps {
  currentDate: Date;
  rdData: SpeedupDatum[];
  width: number;
  height: number;
  deactivateTooltip?: boolean;
}

function getCurvePoints(points: Array<[number, number]>) {
  if (points.length < 2) return ""

  let path = `M ${points[0][0]} ${points[0][1]}`

  for (let i = 0; i < points.length - 1; i++) {
    const current = points[i]
    const next = points[i + 1]

    // Calculate control points that better handle exponential growth
    const dx = next[0] - current[0]
    const dy = next[1] - current[1]

    // Adjust scale based on the rate of change
    const slope = Math.abs(dy / dx)
    const scale = Math.min(0.25, 1 / (1 + slope))

    // Control points are positioned to create smoother curves
    const c1x = current[0] + dx * scale
    const c1y = current[1] + dy * (scale * 0.5)
    const c2x = next[0] - dx * scale
    const c2y = next[1] - dy * (scale * 0.5)

    path += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${next[0]} ${next[1]}`
  }

  return path
}

const defaultData = historicalRdMultipleData.map(([date, [OpenBrain, China, US2nd]]) => ({
  date,
  OpenBrain,
  China,
  US2nd,
}))

export function ComputeUsageByActorLineChart({
  currentDate,
  rdData,
  width,
  height,
  deactivateTooltip = false,
}: ComputeUsageByActorLineChartProps) {
  const [hoveredDataPoint, setHoveredDataPoint] = React.useState<{
    index: number;
    data: SpeedupDatum;
    x: number;
  } | null>(null);

  // Compute the hovered date formatted the same way as our timestamp labels.
  const hoveredDateFormatted = hoveredDataPoint
    ? new Date(hoveredDataPoint.data.date).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      })
    : null;

  const dataSource: SpeedupDatum[] = [...defaultData, ...rdData];
  const deDuplicatedRdData = dataSource.filter((d, i, self) =>
    i === self.findIndex((t) => t.date === d.date)
  );
  // Filter for points up to the current date
  const filteredData = deDuplicatedRdData.filter((d) => new Date(d.date) <= currentDate);
  const baseData = filteredData.length > 0 ? filteredData : [dataSource[0]];

  // Overall SVG dimensions and margins.
  const margin = { top: 10 };

  // New paddings that will not be taken into account for scaling.
  const topPadding = 7;
  const rightPadding = 7;

  // Compute the effective drawing dimensions (scaling area)
  const innerWidth = width - rightPadding;
  const innerHeight = height - margin.top - topPadding;

  // Compute the maximum and minimum values from the visible datapoints (for y scaling)
  const { maxVal, minVal } = useMemo(() => {
    const values = baseData.flatMap((d) => [d.OpenBrain, d.US2nd, d.China]);
    return {
      maxVal: Math.max(...values),
      minVal: Math.min(...values),
    };
  }, [baseData]);

  // For the visible points we distribute them evenly across the innerWidth.
  const xForIndex = (i: number) =>
    baseData.length > 1 ? (i / (baseData.length - 1)) * innerWidth : 0;

  // Given a value, compute the Y coordinate (flipped because SVG origin is top left)
  // Now scales between minVal and maxVal instead of 0 and maxVal
  const yForValue = (value: number) =>
    innerHeight - ((value - minVal) / (maxVal - minVal)) * innerHeight;

  // Define an offset so that the extra (ghost) segment is rendered well outside the viewbox.
  const ghostOffset = 20;

  // Define our series information.
  const series = [
    { key: "OpenBrain", color: "var(--accent)", icon: "/openai.svg" },
    { key: "US2nd", color: "#333", icon: "/world.svg" },
    { key: "China", color: "#333", icon: "/china.svg" },
  ] as const;

  // Compute markers for threshold labels along the "OpenBrain" line.
  const thresholdMarkers = useMemo(() => {
    const markers: Array<{
      label: string;
      value: number;
      x: number;
      y: number;
      timestamp: string;
    }> = []

    for (const t of thresholds) {
      for (let i = 0; i < baseData.length; i++) {
        const currentVal = baseData[i].OpenBrain
        let nextData
        if (i + 1 < baseData.length) {
          nextData = baseData[i + 1]
        } else if (dataSource[i + 1]) {
          nextData = dataSource[i + 1]
        } else {
          nextData = null
        }
        if (!nextData) break
        const nextVal = nextData.OpenBrain

        if (currentVal <= t.value && t.value <= nextVal) {
          const segmentDiff = nextVal - currentVal
          const ratio = (t.value - currentVal) / segmentDiff
          const x1 = xForIndex(i)
          const x2 = xForIndex(i + 1)
          const x = x1 + ratio * (x2 - x1)
          const y = yForValue(t.value)
          const estimatedLabelWidth = t.label.length * 5
          const parsedDate = new Date(nextData.date)
          const formattedDate = parsedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short'})

          if (x > estimatedLabelWidth + 10) {
            markers.push({ label: t.label, value: t.value, x, y, timestamp: formattedDate})
          }
          break
        }
      }
    }

    // Filter out markers where the labels would overlap, using a rough bounding box approximation.
    // For each marker we assume:
    //   left: marker.x - 5 - (marker.label.length * 5)
    //   right: marker.x - 5
    //   top: marker.y - 5
    //   bottom: marker.y - 5 + 10
    // Two markers are considered overlapping if their bounding boxes intersect.
    // We first sort by x coordinate and then only add a new marker if it does not overlap
    // with any marker already added.

    const sortedMarkers = markers.sort((a, b) => b.x - a.x)
    const filteredMarkers: typeof markers = []

    for (const marker of sortedMarkers) {
      const markerBox = {
        left: marker.x - 5 - marker.label.length * 6,
        right: marker.x - 5,
        top: marker.y - 5,
        bottom: marker.y + 5,
      }

      let overlap = false

      

      for (const kept of filteredMarkers) {
        const keptBox = {
          left: kept.x - 5 - kept.label.length * 6,
          right: kept.x - 5,
          top: kept.y - 5,
          bottom: kept.y + 5,
        }

        // Check for box intersection. If the boxes do not lie completely to the left/right or above/below each other, they overlap.
        if (
          !(markerBox.right < keptBox.left ||
            markerBox.left > keptBox.right ||
            markerBox.bottom < keptBox.top ||
            markerBox.top > keptBox.bottom)
        ) {
          overlap = true
          break
        }
      }

      // If the marker is too far left, too far right, or too far up, we just get rid of it.
      if (markerBox.left < 0 || markerBox.top > innerHeight) {
        overlap = true
      }

      if (!overlap) {
        filteredMarkers.push(marker)
      }
    }

    return filteredMarkers
  }, [baseData, maxVal, xForIndex])

  // After the existing thresholdMarkers useMemo
  const visibleTimestampMarkers = useMemo(() => {
    // Sort the markers by x (ascending)
    const sortedMarkers = [...thresholdMarkers].sort((a, b) => a.x - b.x);
    const nonOverlapping: Array<typeof thresholdMarkers[number]> = [];

    for (const marker of sortedMarkers) {
      // Estimate bounding box for the rendered timestamp.
      // Given that the timestamp <motion.text> is translated by (marker.x,20)
      // and uses "textAnchor" of "end", we assume an approximate width
      // of marker.timestamp.length × 7 and a height of 10 (offset by 5px up and 5px down).
      const estimatedWidth = marker.timestamp.length * 6;
      const markerBBox = {
        left: marker.x - 5 - estimatedWidth,
        right: marker.x - 5,
        top: 15,   // (20 - 5)
        bottom: 25 // (20 - 5 + 10)
      };

      let overlap = false;
      for (const kept of nonOverlapping) {
        const keptEstimatedWidth = kept.timestamp.length * 6;
        const keptBBox = {
          left: kept.x - 5 - keptEstimatedWidth,
          right: kept.x - 5,
          top: 15,
          bottom: 25
        };

        // Since timestamp labels all share roughly the same vertical band,
        // we only check horizontal overlap.
        if (!(markerBBox.right < keptBBox.left || markerBBox.left > keptBBox.right)) {
          overlap = true;
          break;
        }
      }

      if (!overlap) {
        nonOverlapping.push(marker);
      }
    }
    return nonOverlapping;
  }, [thresholdMarkers]);

  // Add this function before the return statement
  const getAdjustedLabelPosition = (marker: { x: number; y: number; label: string }) => {
    return {
      x: marker.x - 5,
      y: marker.y - 5,
      anchor: "end" as const
    }
  }

  // Instead of adjusting each logo one at a time inside the map below,
  // compute adjusted offsets for all logos at once using a pooling algorithm.
  const logoOffsets = useMemo(() => {
    const minSpacing = 15;
    // Get each logo's base Y position from the last datapoint.
    const logos = series.map((s) => {
      const baseY = yForValue(
        Number(baseData[baseData.length - 1][s.key as keyof SpeedupDatum])
      );
      return { key: s.key, baseY };
    });

    // Sort logos by their base Y (ascending).
    const sorted = [...logos].sort((a, b) => a.baseY - b.baseY);

    // Start with the original base positions.
    const positions = sorted.map((logo) => logo.baseY);

    // Forward pass: ensure each logo is at least `minSpacing` below the previous one.
    for (let i = 1; i < positions.length; i++) {
      positions[i] = Math.max(positions[i], positions[i - 1] + minSpacing);
    }
    // Backward pass: adjust positions so the displacements are minimal.
    for (let i = positions.length - 2; i >= 0; i--) {
      positions[i] = Math.min(positions[i], positions[i + 1] - minSpacing);
    }

    // Map the adjustments back to a dictionary keyed by logo key.
    const offsets: Record<string, number> = {};
    sorted.forEach((logo, i) => {
      offsets[logo.key] = positions[i] - logo.baseY - 2;
    });
    return offsets;
  }, [baseData, innerHeight, minVal, maxVal]);

  // Add function to handle mouse movement
  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const svgRect = event.currentTarget.getBoundingClientRect();
    const mouseX = event.clientX - svgRect.left;

    // Calculate the nearest data index by rounding the proportional value.
    let dataIndex = Math.ceil((mouseX / innerWidth) * (baseData.length - 1));
    dataIndex = Math.max(0, Math.min(dataIndex, baseData.length - 1));

    setHoveredDataPoint({
      index: dataIndex,
      data: baseData[dataIndex],
      x: xForIndex(dataIndex),
    });
  };

  const tooltipContent = hoveredDataPoint && (
    <div className="w-140 p-4 font-sans text-xs">
      <p className="mb-2 text-xs mt-0 text-justify">
        This graph shows how much AI systems are speeding up the process of AI research,
        compared to a baseline of human researchers. We also highlight milestones in
        general capabilities as AI systems achieve them.
      </p>
      <p className="mb-2 text-xs mt-0 text-justify">
        As of <b>{new Date(hoveredDataPoint.data.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short'})}</b>, the AI systems provide the following speedups:
      </p>
      <div className="space-y-1 mt-3 pt-3 font-mono">
        <div className="font-bold flex">
          <OpenAILogo className="w-4 h-4 mr-2" /> OpenBrain: <span className="ml-auto">{hoveredDataPoint.data.OpenBrain.toFixed(2)}x</span>
        </div>
        <div className="flex">
          <ChinaLogo className="w-4 h-4 mr-2" /> Leading Chinese Lab: <span className="ml-auto">{hoveredDataPoint.data.China.toFixed(2)}x</span>
        </div>
        <div className="flex">
          <WorldLogo className="w-4 h-4 mr-2" /> Best public model: <span className="ml-auto">{hoveredDataPoint.data.US2nd.toFixed(2)}x</span>
        </div>
      </div>
    </div>
  );

  const {ref, getReferenceProps, getFloatingProps, floatingRef, floatingStyles, open} = useTooltip({placement: "bottom-end", interactive: true});

  return (
    <div style={{ width, height: height + 30, position: "relative" }} className="[grid-area:rdmultiple] mb-1">
      <FloatingPortal>
        {open && !deactivateTooltip && <div ref={floatingRef} {...getFloatingProps()} style={floatingStyles} className="bg-[var(--accent)] text-white rounded-lg shadow-lg z-[1000] text-xs font-mono max-w-[300px]">
          {tooltipContent}
        </div>}
      </FloatingPortal>
      <motion.svg
        ref={ref}
        {...getReferenceProps()}
        width={width}
        height={height + 30}
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        onMouseMove={handleMouseMove}
        onMouseOut={() => setHoveredDataPoint(null)}
      >
        <defs>
          {/* Clip the drawing of the lines to the inner (scaled) region */}
          <clipPath id="revealClip">
            <rect x={0} y={0} width={innerWidth} height={innerHeight} />
          </clipPath>
        </defs>

        {/* Group for lines */}
        <g clipPath="url(#revealClip)" transform={`translate(0, ${margin.top + topPadding})`}>
          {series.map(s => (
            <React.Fragment key={s.key}>
              {baseData.slice(0, baseData.length).map((d, i) => {
                const [x, y] = [xForIndex(i), yForValue(Number(d[s.key as keyof SpeedupDatum]))];
                const points: Array<[number, number]> = [
                  [x, y],
                  [
                    xForIndex(i + 1),
                    baseData[i + 1]
                      ? yForValue(Number(baseData[i + 1][s.key as keyof SpeedupDatum]))
                      : y,
                  ],
                ];
                const pathD = getCurvePoints(points);
                // Determine segment opacity: if there's a hovered point, only the segment at the hovered index
                // or the segment immediately before it stays fully opaque; others are faded.
                const isHighlighted = !hoveredDataPoint || (i === hoveredDataPoint.index - 1);
                const segmentOpacity = isHighlighted ? 0.8 : 0.2;
                return (
                  <motion.path
                    key={`${s.key}-line-${i}`}
                    d={pathD}
                    stroke={s.color}
                    strokeWidth={2.5}
                    fill="none"
                    initial={{ d: pathD, strokeOpacity: segmentOpacity }}
                    animate={{ d: pathD, strokeOpacity: segmentOpacity }}
                    transition={{ duration: 0.5, ease: "linear" }}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </g>

        {/* Group for circles & logos */}
        <g transform={`translate(0, ${margin.top + topPadding})`}>
          {/* Render all background circles */}
          {series.map(s => {
            const cx = xForIndex(baseData.length - 1);
            const cy = yForValue(Number(baseData[baseData.length - 1][s.key as keyof SpeedupDatum]));
            return (
              <motion.circle
                key={`${s.key}-circle-bg`}
                cx={cx}
                cy={cy}
                r={10}
                fill="white"
                initial={{ cx, cy }}
                animate={{ cx, cy }}
                transition={{ duration: 0.5, ease: "linear" }}
              />
            );
          })}

          {/* Render logos with improved spreading */}
          {series.map((s) => {
            const cx = xForIndex(baseData.length - 1);
            const cy = yForValue(Number(baseData[baseData.length - 1][s.key as keyof SpeedupDatum]));
            // Use the computed offset from the pooling algorithm
            const offsetY = logoOffsets[s.key] ?? 0;

            return (
              <image
                key={`${s.key}-logo`}
                href={s.icon}
                width={14}
                height={14}
                x={cx - 7}
                y={cy - 7 + offsetY}
                className="transition-all duration-500 ease-linear"
              />
            );
          })}
        </g>

        {/* Group for threshold labels & markers */}
        <g transform={`translate(0, ${margin.top + topPadding})`}>
          <AnimatePresence>
            {[...thresholdMarkers].sort((a, b) => a.x - b.x).map(marker => {
              const labelPos = getAdjustedLabelPosition(marker);
              return (
                <motion.g
                  layoutId={`threshold-marker-${marker.label}`}
                  key={`threshold-marker-${marker.label}`}
                  exit={{ opacity: 0, y: -10 }}
                  initial={{ opacity: 1, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  data-label={marker.label}
                >
                  <circle
                    key={`threshold-marker-circle-${marker.label}`}
                    r={1}
                    fill="white"
                    cx={marker.x}
                    cy={marker.y}
                    className="transition-all duration-500 ease-linear"
                  />
                  <text
                    key={`threshold-marker-text-${marker.label}`}
                    fill="black"
                    fontSize={10}
                    style={{ pointerEvents: "none", transform: `translate(${labelPos.x}px, ${labelPos.y}px)` }}
                    textAnchor={labelPos.anchor}
                    className="font-mono font-bold transition-transform transition-opacity duration-500 ease-linear"
                  >
                    {marker.label}
                  </text>
                </motion.g>
              );
            })}
          </AnimatePresence>
        </g>

        {/* Timestamp labels at the bottom */}
        <g transform={`translate(0, ${height - 5})`}>
          <AnimatePresence>
            {visibleTimestampMarkers.map(marker => {
              // If a hovered date exists, show full opacity only if the marker matches.
              const markerOpacity = hoveredDateFormatted
                ? marker.timestamp === hoveredDateFormatted
                  ? 1
                  : 0.2
                : marker.x > innerWidth
                ? 0
                : 1;
              return (
                <motion.g
                  key={`timestamp-${marker.label}`}
                  initial={{ opacity: markerOpacity, y: 0 }}
                  animate={{ opacity: markerOpacity, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                >
                  <text
                    fill="black"
                    fontSize={10}
                    textAnchor="end"
                    className={`font-mono ${
                      hoveredDateFormatted && marker.timestamp === hoveredDateFormatted
                        ? "font-bold"
                        : "font-normal"
                      } transition-all duration-500 ease-linear`}
                      style={{ pointerEvents: "none", transform: `translate(${marker.x}px, 20px)`}}
                  >
                    {marker.timestamp}
                  </text>
                </motion.g>
              );
            })}
          </AnimatePresence>

          {/* If the hovered date does not match any marker, render it directly */}
          {hoveredDataPoint &&
            !visibleTimestampMarkers.some(
              (marker) => marker.timestamp === hoveredDateFormatted
            ) && (
              <motion.text
                fill="black"
                fontSize={10}
                textAnchor="end"
                className="font-mono font-bold transition-all duration-500 ease-linear"
                transform={`translate(${hoveredDataPoint.x}, 20)`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {hoveredDateFormatted}
              </motion.text>
            )}
        </g>
      </motion.svg>
    </div>
  );
}